import { useEffect, useRef } from "react"

import { usePrefersReducedMotion } from "./usePrefersReducedMotion"

/**
 * Zone dans laquelle la souris est suivie :
 * - "element" : uniquement au survol de l'élément, qui revient à plat quand la souris sort ;
 * - "window" : partout dans la fenêtre.
 */
export type TiltTracking = "element" | "window"

export interface UseTiltOptions {
  tracking?: TiltTracking
  /** Inclinaison maximale en degrés */
  maxTilt?: number
}

const DEFAULT_MAX_TILT = 8
const CENTER = 0.5

/** Écrit les variables CSS d'inclinaison et de reflet à partir d'une position relative (0 à 1) */
const applyTilt = (
  element: HTMLElement,
  ratioX: number,
  ratioY: number,
  maxTilt: number,
) => {
  // Décalage par rapport au centre, entre -0.5 et 0.5
  const offsetX = ratioX - CENTER
  const offsetY = ratioY - CENTER
  element.style.setProperty("--clux-tilt-x", `${-offsetY * maxTilt * 2}deg`)
  element.style.setProperty("--clux-tilt-y", `${offsetX * maxTilt * 2}deg`)
  element.style.setProperty("--clux-glare-x", `${ratioX * 100}%`)
  element.style.setProperty("--clux-glare-y", `${ratioY * 100}%`)
}

/**
 * Incline un élément en 3D selon la position de la souris.
 * Les valeurs sont écrites dans des variables CSS (--clux-tilt-x, --clux-tilt-y,
 * --clux-glare-x, --clux-glare-y) pour éviter un rendu React à chaque mouvement.
 * Désactivé si l'utilisateur préfère réduire les animations.
 */
export const useTilt = <T extends HTMLElement>({
  tracking = "element",
  maxTilt = DEFAULT_MAX_TILT,
}: UseTiltOptions = {}) => {
  const ref = useRef<T>(null)
  const prefersReducedMotion = usePrefersReducedMotion()

  useEffect(() => {
    const element = ref.current
    if (!element || prefersReducedMotion) {
      return undefined
    }

    let frameId = 0
    const schedule = (ratioX: number, ratioY: number) => {
      cancelAnimationFrame(frameId)
      frameId = requestAnimationFrame(() =>
        applyTilt(element, ratioX, ratioY, maxTilt),
      )
    }

    const handleWindowMove = (event: MouseEvent) =>
      schedule(
        event.clientX / window.innerWidth,
        event.clientY / window.innerHeight,
      )

    const handleElementMove = (event: MouseEvent) => {
      const rect = element.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) {
        return
      }
      schedule(
        (event.clientX - rect.left) / rect.width,
        (event.clientY - rect.top) / rect.height,
      )
    }

    const handleElementLeave = () => schedule(CENTER, CENTER)

    if (tracking === "window") {
      window.addEventListener("mousemove", handleWindowMove, { passive: true })
    } else {
      element.addEventListener("mousemove", handleElementMove, {
        passive: true,
      })
      element.addEventListener("mouseleave", handleElementLeave)
    }

    return () => {
      cancelAnimationFrame(frameId)
      window.removeEventListener("mousemove", handleWindowMove)
      element.removeEventListener("mousemove", handleElementMove)
      element.removeEventListener("mouseleave", handleElementLeave)
    }
  }, [prefersReducedMotion, tracking, maxTilt])

  return ref
}
